// National subscriber digit counts per country (excluding the country code).
// Single number = exact length; [min, max] = accepted range.
// Mirrored in pocketbase/hooks/phone-validation.js — keep both in sync.

export const NATIONAL_DIGITS: Record<string, number | [number, number]> = {
  '+34': 9, // ES
  '+52': 10, // MX
  '+54': [10, 11], // AR
  '+57': 10, // CO
  '+56': 9, // CL
  '+51': 9, // PE
  '+593': 9, // EC
  '+58': 10, // VE
  '+598': [8, 9], // UY
  '+595': 9, // PY
  '+591': 8, // BO
  '+506': 8, // CR
  '+507': [7, 8], // PA
  '+502': 8, // GT
  '+504': 8, // HN
  '+503': 8, // SV
  '+505': 8, // NI
  '+1': 10, // US/CA/DO/PR (NANP)
  '+53': 8, // CU
  '+55': [10, 11], // BR
  '+351': 9, // PT
  '+33': 9, // FR
  '+49': [10, 11], // DE
  '+39': [9, 10], // IT
  '+44': 10, // GB
  '+31': 9, // NL
  '+32': [8, 9], // BE
  '+41': 9, // CH
  '+43': [10, 11], // AT
  '+353': 9, // IE
  '+30': 10, // GR
  '+46': [7, 10], // SE
  '+47': 8, // NO
  '+45': 8, // DK
  '+358': [9, 10], // FI
  '+48': 9, // PL
  '+7': 10, // RU
  '+380': 9, // UA
  '+81': 10, // JP
  '+86': 11, // CN
  '+91': 10, // IN
  '+82': [9, 10], // KR
  '+90': 10, // TR
  '+972': 9, // IL
  '+971': 9, // AE
  '+212': 9, // MA
  '+240': 9, // GQ
  '+27': 9, // ZA
  '+61': 9, // AU
  '+64': [8, 10], // NZ
};

export type PhoneValidationCode =
  | 'phone_format'
  | 'phone_too_short'
  | 'phone_too_long';

export type PhoneValidation =
  | { ok: true }
  | { ok: false; code: PhoneValidationCode };

function getRange(spec: number | [number, number]): [number, number] {
  return typeof spec === 'number' ? [spec, spec] : spec;
}

function findPrefix(phone: string): string | null {
  const keys = Object.keys(NATIONAL_DIGITS).sort((a, b) => b.length - a.length);
  return keys.find((k) => phone.startsWith(k)) ?? null;
}

export function validatePhone(phone: string): PhoneValidation {
  if (!/^\+\d{6,16}$/.test(phone)) return { ok: false, code: 'phone_format' };
  const prefix = findPrefix(phone);
  if (!prefix) {
    return /^\+\d{7,15}$/.test(phone)
      ? { ok: true }
      : { ok: false, code: 'phone_format' };
  }
  const digits = phone.slice(prefix.length).length;
  const spec = NATIONAL_DIGITS[prefix];
  if (spec === undefined) return { ok: true };
  const [min, max] = getRange(spec);
  if (digits < min) return { ok: false, code: 'phone_too_short' };
  if (digits > max) return { ok: false, code: 'phone_too_long' };
  return { ok: true };
}
